import { Link } from 'react-router-dom';

import {
  useReactionTime,
  useCostToWin,
  useCrawlProgress,
  useDataQuality,
  useFreshness,
  useRankHistory,
  useRepricing,
  useTransportSpend,
  useVolatility,
} from '../api/queries';
import { CostToWinScatter } from '../charts/CostToWinScatter';
import { FreshnessHistogram } from '../charts/FreshnessHistogram';
import { ScanActivityChart } from '../charts/ScanActivityChart';
import { CrawlSpendChart } from '../charts/CrawlSpendChart';
import { RankHistoryArea } from '../charts/RankHistoryArea';
import { RankedBars } from '../charts/RankedBars';
import type { RankedBarRow } from '../charts/RankedBars';
import { DIVERGE, SERIES, STATUS } from '../charts/palette';
import { Card } from '../components/Card';
import { CompositionBar } from '../components/CompositionBar';
import { Failed, Loading } from '../components/States';
import { StatTile } from '../components/StatTile';
import { humanHours } from '../utils/duration';
import { count, money, plainPercent } from '../utils/format';

const CONFIDENCE_ORDER = [
  'certain (EAN proven)',
  'high (0.85+)',
  'good (0.70+)',
  'borderline (under 0.70)',
];

const CONFIDENCE_COLORS: Record<string, string> = {
  'certain (EAN proven)': STATUS.good,
  'high (0.85+)': SERIES[0],
  'good (0.70+)': STATUS.warning,
  'borderline (under 0.70)': STATUS.critical,
};

export function InsightsPage() {
  const costToWin = useCostToWin(40);
  const reaction = useReactionTime(180, 15);
  const rankHistory = useRankHistory(60);
  const volatility = useVolatility(15);
  const repricing = useRepricing(15);
  const quality = useDataQuality();
  const freshness = useFreshness();
  const progress = useCrawlProgress();
  const spend = useTransportSpend(14);

  const volatilityBars: RankedBarRow[] = (volatility.data ?? []).map((row) => ({
    label: row.name.length > 46 ? `${row.name.slice(0, 45)}…` : row.name,
    value: row.spreadPct,
    detail: [
      { key: 'spread', label: 'Swing', value: `${row.spreadPct.toFixed(1)}%` },
      { key: 'eur', label: 'In euros', value: money(row.spreadEur) },
      { key: 'low', label: 'Lowest seen', value: money(row.lowestSeen) },
      { key: 'high', label: 'Highest seen', value: money(row.highestSeen) },
      { key: 'obs', label: 'Scans', value: String(row.observations) },
    ],
  }));

  const repricingBars: RankedBarRow[] = (repricing.data ?? []).map((row) => ({
    label: row.shopName,
    value: row.changeRatePct ?? 0,
    detail: [
      { key: 'rate', label: 'Moves price', value: plainPercent(row.changeRatePct, 1) },
      { key: 'changes', label: 'Changes seen', value: String(row.changes) },
      { key: 'obs', label: 'Observations', value: String(row.observations) },
      { key: 'size', label: 'Mean move', value: plainPercent(row.avgChangePct, 1) },
      { key: 'products', label: 'Products', value: String(row.productsSeen) },
    ],
  }));

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h1 className="page__title">Insights</h1>
          <p className="page__lede">
            The questions a snapshot cannot answer: which wins are cheapest, whether your position is drifting,
            how much the numbers can be trusted, and what collecting them costs.
          </p>
        </div>
      </header>

      <Card
        title="Cheapest wins available"
        note="How little it would cost to take rank 1 — bottom-right is a few cents against a crowded listing"
        action={
          <Link to="/recommendations" className="button">
            Price moves
          </Link>
        }
      >
        {costToWin.isError ? (
          <Failed error={costToWin.error} />
        ) : costToWin.isLoading ? (
          <Loading />
        ) : (
          <>
            <CostToWinScatter rows={costToWin.data ?? []} />
            {(costToWin.data?.length ?? 0) > 0 && (
              <div className="table-wrap" style={{ marginTop: 'var(--space-4)' }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="num">Cut needed</th>
                      <th className="num">Of price</th>
                      <th className="num">Your price</th>
                      <th className="num">Cheapest</th>
                      <th>Held by</th>
                      <th className="num">Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(costToWin.data ?? []).slice(0, 10).map((row) => (
                      <tr key={row.shopProductId}>
                        <td>
                          <Link to={`/products/${row.shopProductId}`} className="cell-product">
                            {row.imageUrl && (
                              <img className="cell-product__img" src={row.imageUrl} alt="" loading="lazy" />
                            )}
                            <span className="cell-product__name">{row.name}</span>
                          </Link>
                        </td>
                        <td className="num" style={{ fontWeight: 600 }}>
                          {money(row.costToWinEur)}
                        </td>
                        <td className="num">{plainPercent(row.costToWinPct, 1)}</td>
                        <td className="num">{money(row.ourPrice)}</td>
                        <td className="num">{money(row.minPrice)}</td>
                        <td className="secondary">{row.cheapestShop ?? '—'}</td>
                        <td className="num">
                          #{row.ourRank} / {row.offerCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="Where you sit, week by week" note="Are you drifting down the listings?">
        {rankHistory.isError ? (
          <Failed error={rankHistory.error} />
        ) : rankHistory.isLoading ? (
          <Loading />
        ) : (
          <RankHistoryArea points={rankHistory.data ?? []} />
        )}
      </Card>

      <div className="grid grid--halves">
        <Card title="Most volatile products" note="Swing in the market low across scans; needs 3+ scans">
          {volatility.isError ? (
            <Failed error={volatility.error} />
          ) : volatility.isLoading ? (
            <Loading />
          ) : volatilityBars.length === 0 ? (
            <div className="state">
              <div>Not enough history yet.</div>
              <div className="muted">
                A product needs at least three successful scans before a swing means anything.
              </div>
            </div>
          ) : (
            <RankedBars rows={volatilityBars} color={SERIES[3]} unit="%" />
          )}
        </Card>

        <Card title="Who repriced" note="Share of observations where a shop's price had moved">
          {repricing.isError ? (
            <Failed error={repricing.error} />
          ) : repricing.isLoading ? (
            <Loading />
          ) : repricingBars.length === 0 ? (
            <div className="state">
              <div>No repeat observations yet.</div>
              <div className="muted">
                Detecting a reprice needs the same shop seen twice on the same product.
              </div>
            </div>
          ) : (
            <RankedBars rows={repricingBars} color={DIVERGE.high} unit="%" />
          )}
        </Card>

      <Card
        title="Do you lead the market or follow it?"
        note="When the market low moves, how many days pass before your own price changes. Fastest reactions first; needs 3+ market moves."
      >
        {reaction.isLoading ? (
          <Loading />
        ) : reaction.isError ? (
          <Failed error={reaction.error} />
        ) : (reaction.data?.length ?? 0) === 0 ? (
          <div className="state">
            <div>No product has both a market move and a price change of your own yet.</div>
            <div className="muted">This needs scan history and at least one price change in the feed.</div>
          </div>
        ) : (
          <RankedBars
            rows={(reaction.data ?? []).map((row) => ({
              label: row.name,
              value: row.avgReactionDays,
              detail: [
                { key: 'moves', label: 'Market moved', value: `${count(row.marketMoves)} times` },
                { key: 'ours', label: 'You repriced', value: `${count(row.ourMoves)} times` },
                { key: 'fast', label: 'Fastest reaction', value: `${row.fastestDays} d` },
                { key: 'slow', label: 'Slowest reaction', value: `${row.slowestDays} d` },
              ],
            }))}
            color={SERIES[6]}
            unit=" d"
          />
        )}
      </Card>
      </div>

      <Card
        title="How fresh is the data?"
        note="Every active product placed by the age of its newest successful scan"
      >
        {freshness.isError ? (
          <Failed error={freshness.error} />
        ) : freshness.isLoading ? (
          <Loading />
        ) : (
          freshness.data && (
            <div className="stack">
              <div className="grid grid--tiles">
                <StatTile
                  label="Typical age"
                  value={
                    freshness.data.medianAgeHours === null ? '—' : humanHours(freshness.data.medianAgeHours)
                  }
                  foot={`half the scanned products are fresher; oldest is ${
                    freshness.data.oldestAgeHours === null
                      ? '—'
                      : humanHours(freshness.data.oldestAgeHours)
                  }`}
                />
                <StatTile
                  label="Due a rescan"
                  value={count(freshness.data.overdue)}
                  foot={`older than the ${Math.round(freshness.data.rescanAfterHours / 24)}-day window`}
                  accent={freshness.data.overdue > 0 ? STATUS.warning : STATUS.good}
                />
                <StatTile
                  label="No usable scan"
                  value={count(freshness.data.neverAttempted + freshness.data.attemptedNeverSucceeded)}
                  foot={
                    freshness.data.attemptedNeverSucceeded > 0
                      ? `${count(freshness.data.attemptedNeverSucceeded)} tried and never succeeded, ${count(
                          freshness.data.neverAttempted,
                        )} never attempted`
                      : 'none attempted yet'
                  }
                  accent={freshness.data.attemptedNeverSucceeded > 0 ? STATUS.critical : undefined}
                />
                <StatTile
                  label="Covered"
                  value={plainPercent(
                    freshness.data.activeProducts === 0
                      ? 0
                      : (freshness.data.scannedProducts / freshness.data.activeProducts) * 100,
                  )}
                  foot={`${count(freshness.data.scannedProducts)} of ${count(
                    freshness.data.activeProducts,
                  )} products have a usable scan`}
                />
              </div>

              <FreshnessHistogram
                buckets={freshness.data.buckets}
                rescanAfterHours={freshness.data.rescanAfterHours}
              />

              <div>
                <div className="card__title" style={{ marginBottom: 'var(--space-3)' }}>
                  Scans per day, last 30 days
                </div>
                <p className="card__text">
                  Whether that staleness is being worked off. A tall red day means the crawler ran and
                  learned nothing.
                </p>
                <ScanActivityChart days={freshness.data.daily} />
              </div>
            </div>
          )
        )}
      </Card>

      <Card
        title="Can you trust these numbers?"
        note="Match confidence, and comparisons already invalidated by a price change"
      >
        {quality.isError ? (
          <Failed error={quality.error} />
        ) : quality.isLoading ? (
          <Loading />
        ) : (
          quality.data && (
            <div className="stack">
              <div className="grid grid--tiles">
                <StatTile
                  label="Repriced since the scan"
                  value={count(quality.data.staleComparisons)}
                  foot="gap and rank are recomputed against your current price; the rival prices are as old as the scan"
                />
                <StatTile
                  label="Borderline matches"
                  value={count(quality.data.borderlineMatches)}
                  foot="accepted only just above the confidence threshold"
                  accent={quality.data.borderlineMatches > 0 ? STATUS.warning : STATUS.good}
                />
                <StatTile
                  label="Coverage"
                  value={`${count(quality.data.scannedProducts)} / ${count(quality.data.activeProducts)}`}
                  foot="products with a usable comparison"
                />
              </div>

              <div>
                <div className="card__title" style={{ marginBottom: 'var(--space-3)' }}>
                  How sure the matcher was
                </div>
                <CompositionBar
                  buckets={quality.data.confidence}
                  order={CONFIDENCE_ORDER}
                  colors={CONFIDENCE_COLORS}
                  emptyLabel="No matched products yet."
                />
              </div>
            </div>
          )
        )}
      </Card>

      <Card title="Crawl economics" note="Throughput, coverage and what the data costs to collect">
        {progress.isError ? (
          <Failed error={progress.error} />
        ) : progress.isLoading ? (
          <Loading />
        ) : (
          progress.data && (
            <div className="stack">
              <div className="grid grid--tiles">
                <StatTile
                  label="First pass remaining"
                  value={count(progress.data.neverAttempted)}
                  foot={
                    progress.data.projectedDaysToComplete === null
                      ? 'not enough history to project'
                      : `about ${progress.data.projectedDaysToComplete} day(s) at the current rate`
                  }
                  accent={SERIES[0]}
                />
                <StatTile
                  label="Requests per day"
                  value={count(progress.data.requestsPerDay)}
                  foot={`${count(progress.data.requestsPerProduct)} per scan on average`}
                />
                <StatTile
                  label="Match rate"
                  value={plainPercent(progress.data.matchedShare, 1)}
                  foot={`${count(progress.data.attemptedProducts)} products attempted`}
                />
                <StatTile
                  label="Requests that found nothing"
                  value={plainPercent(progress.data.wastedRequestShare, 1)}
                  foot="spent on products Heureka does not list"
                  accent={
                    (progress.data.wastedRequestShare ?? 0) > 40 ? STATUS.critical : STATUS.good
                  }
                />
              </div>

              {(progress.data.wastedRequestShare ?? 0) > 40 && (
                <div className="banner">
                  <span aria-hidden="true">◷</span>
                  <div>
                    <strong>
                      {plainPercent(progress.data.wastedRequestShare, 0)} of requests are spent on products
                      Heureka does not list.
                    </strong>
                    <div className="muted" style={{ marginTop: 4 }}>
                      Those products are rescanned on the same routine cycle as everything else. Backing off confirmed misses would roughly double the useful throughput
                      of the same request budget — and cut the paid-transport bill by the same share.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </Card>

      <Card title="Crawl activity and paid escalations" note="Last 14 days">
        {spend.isError ? (
          <Failed error={spend.error} />
        ) : spend.isLoading ? (
          <Loading />
        ) : (
          <CrawlSpendChart points={spend.data ?? []} />
        )}
      </Card>
    </div>
  );
}
